import { useEffect, useMemo, useState } from "react";

import { PalButton, PalTypography } from "@palamar/fe-library";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Box, Grid, lighten } from "@mui/material";

import { ReactComponent as ShopifyIcon } from "~assets/images/onboarding/shopify.svg";
import useLoading from "~common/hooks/useLoading";
import useQueryParams from "~common/hooks/useQueryParams";
import { fetchDirectoryList } from "~store/productQA/actions";
import {
  getShopifyDeepLink,
  getShopifySubscription,
  postShopifyIntegrationCallback,
} from "~store/settings/eCommerce/actions";
import { getProject } from "~store/settings/projectSettings/actions";
import { selectProject } from "~store/settings/projectSettings/selectors";

const StepNumberComp = ({ stepNumber }) => {
  return (
    <Box
      sx={{
        // position: "absolute",
        // top: 0,
        // left: 0,
        borderRadius: "50px",
        backgroundColor: (theme) => theme.palette.primary.main,
        padding: (theme) => theme.spacing(1),
        fontSize: (theme) => theme.typography.h5.fontSize,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        position: "absolute",
        marginLeft: "-20px",
        // marginTop: "-4px",
      }}
    >
      {stepNumber}
    </Box>
  );
};

function StepShopifyCompleted({ nextAction, shopifyIntegrationId: shopifyIntegrationIdProp }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, q] = useLoading(false);
  const project = useSelector(selectProject);

  const [projectDirectoryList, setProjectDirectoryList] = useState([]);

  const { shopifyCode, shopifyInt, shopifyConnDone, shopifyConnId } = useQueryParams();

  // const location = useLocation();

  const shopifyIntegrationId = shopifyIntegrationIdProp || project?.shopify_integration_id;

  //  const shopifyIntegrationParams = location?.state?.shopifyIntegrationParams;
  const isShopifyConnectedDone = shopifyConnDone === "true";
  const shopifyConnectedProjectId = !!shopifyConnId && Number(shopifyConnId);

  useEffect(() => {
    if (project?.id) {
      dispatch(fetchDirectoryList(project.id, "")).then((response) => {
        setProjectDirectoryList(response?.results || []);
      });
    }
  }, [dispatch, project?.id]);

  const shopifyDirectoryInfo = useMemo(() => {
    return projectDirectoryList.filter((item) => item.name === "default")?.[0] || {};
  }, [projectDirectoryList]);

  useEffect(() => {
    if (shopifyConnectedProjectId) {
      q(dispatch(getProject(shopifyConnectedProjectId)));
    }
  }, [t, q, dispatch, shopifyConnectedProjectId]);

  const [subscriptionInfo, setSubscriptionInfo] = useState({
    subscription: {
      // id: 30,
      // pricing_plan: {
      //   id: 2,
      //   name: "Pro",
      //   handle: "pro",
      //   interval: "EVERY_30_DAYS",
      //   price: "49.99",
      //   currency: "USD",
      //   free_trial_days: 7,
      //   limits: [
      //     {
      //       id: 2,
      //       pricing_plan: 2,
      //       usage_type: "customer_message",
      //       limit: null,
      //       limit_type: "PERIODIC",
      //       interval: "MONTHLY",
      //     },
      //   ],
      // },
      // subscription_id: "gid://shopify/AppSubscription/23244505137",
      // shopify_integration: 4,
      // status: "ACTIVE",
      // current_period_end: "2025-06-11T11:42:51.000000+0300",
      // free_trial_days: 4,
      // updated_at: "2025-05-08T11:42:51.000000+0300",
      // created_at: "2025-05-08T11:42:49.000000+0300",
    },
    plan_page_link: "https://admin.shopify.com/store/palmate-dev/charges/palmate-extension-only-1/pricing_plans",
    deep_link:
      "https://palmate-dev.myshopify.com/admin/themes/current/editor?context=apps&activateAppId=a77b490a-98b1-47df-97f8-e6d35c6dbf08/chatbot",
  });

  const handleRedirectToShopify = async (e) => {
    await q(nextAction(e, { preventRedirect: true }));
    const deepLinkResponse = await q(dispatch(getShopifyDeepLink(shopifyIntegrationId)));
    if (deepLinkResponse?.deep_link) {
      window.open(deepLinkResponse?.deep_link, "_blank");
    }
  };

  const handleRedirectToSubscription = async (e) => {
    if (subscriptionInfo?.plan_page_link) {
      window.open(subscriptionInfo?.plan_page_link, "_blank");
    }
  };

  useEffect(() => {
    if (!shopifyIntegrationId) return;
    (async () => {
      const deepLinkResponse = await q(dispatch(getShopifySubscription(shopifyIntegrationId)));
      if (deepLinkResponse) {
        setSubscriptionInfo(deepLinkResponse);
      }
    })();
  }, [dispatch, q, shopifyIntegrationId]);

  useEffect(() => {
    if (shopifyCode && isShopifyConnectedDone) {
      (async () => {
        dispatch(
          postShopifyIntegrationCallback(
            {
              code: shopifyCode,
            },
            {
              noAlert: true,
            }
          )
        );
      })();
    }
  }, [dispatch, shopifyCode, isShopifyConnectedDone]);

  return (
    <Box p={4} textAlign="center">
      <Grid container justifyContent="center" spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              maxWidth: 225,
              width: "100%",
              margin: "0 auto",
            }}
          >
            <ShopifyIcon />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <PalTypography color="textPrimary" fontWeight={600} variant="h5">
            {t("dashboard.onboarding.shopifyCompletedTitle")}
          </PalTypography>
          {/* <PalTypography gutterBottom variant="body1">
            {t("dashboard.onboarding.shopifyCompletedDescription")}
          </PalTypography> */}
        </Grid>
        <Grid item position={"relative"} xs={12}>
          <Box display="flex">
            <StepNumberComp stepNumber={1} />
            <PalTypography
              fontWeight={600}
              sx={{
                textAlign: "center",
                width: "100%",
              }}
              variant="h6"
            >
              {t("dashboard.onboarding.goToSubscriptionText")}
            </PalTypography>
          </Box>

          {subscriptionInfo?.subscription ? (
            <>
              <PalTypography gutterBottom variant="body1">
                {t("dashboard.onboarding.shopifyPlan")}
                <Box
                  sx={{
                    fontWeight: 700,
                    borderRadius: 1,
                    padding: (theme) => theme.spacing(0, 1),
                    margin: (theme) => theme.spacing(0, 1),
                    display: "inline-block",
                    ...(subscriptionInfo?.subscription?.pricing_plan?.handle === "free" && {
                      backgroundColor: (theme) => lighten(theme.palette.info.main, 0.8),
                      color: (theme) => theme.palette.info.main,
                    }),
                    ...(subscriptionInfo?.subscription?.pricing_plan?.handle === "basic" && {
                      backgroundColor: (theme) => lighten(theme.palette.secondary.main, 0.8),
                      color: (theme) => theme.palette.secondary.main,
                    }),

                    ...(subscriptionInfo?.subscription?.pricing_plan?.handle === "pro" && {
                      backgroundColor: lighten("#a8871e", 0.8),
                      color: "#a8871e",
                    }),
                  }}
                >
                  {subscriptionInfo?.subscription?.pricing_plan?.name}
                </Box>
              </PalTypography>
              <Box
                mb={1}
                sx={{
                  marginTop: 1,
                  fontSize: "12px",
                }}
              >
                {t("dashboard.onboarding.shopifyPlanInterval")}:&nbsp;
                <b>
                  {subscriptionInfo?.subscription?.pricing_plan?.interval === "EVERY_30_DAYS"
                    ? t("dashboard.onboarding.shopifyMonthly")
                    : t("dashboard.onboarding.shopifyYearly")}
                </b>
              </Box>
            </>
          ) : (
            <PalTypography gutterBottom color="textSecondary" variant="body2">
              {t("dashboard.onboarding.shopifyPlanNotFound")}
            </PalTypography>
          )}

          <PalButton
            color="primary"
            disabled={loading}
            loading={loading}
            size="medium"
            variant="contained"
            onClick={handleRedirectToSubscription}
          >
            {t("dashboard.onboarding.shopifySubscriptionButton")}
          </PalButton>
        </Grid>
        <Grid item mt={4} position={"relative"} xs={12}>
          <Box display="flex" mb={1}>
            <StepNumberComp stepNumber={2} />
            <PalTypography
              fontWeight={600}
              sx={{
                textAlign: "center",
                width: "100%",
              }}
              variant="h6"
            >
              {t("dashboard.onboarding.goToStorefrontText")}
            </PalTypography>
          </Box>

          <PalButton
            color="primary"
            disabled={loading}
            loading={loading}
            size="medium"
            variant="contained"
            onClick={handleRedirectToShopify}
          >
            {t("dashboard.onboarding.shopifyRedirectButton")}
          </PalButton>
        </Grid>
        <Grid item xs={12}>
          {typeof shopifyDirectoryInfo?.remaining_new_products === "number" ? (
            shopifyDirectoryInfo.remaining_new_products > 0 ? (
              <PalTypography gutterBottom color="primary.main" variant="body2">
                {t("dashboard.onboarding.shopifySyncInProgress").format(shopifyDirectoryInfo.remaining_new_products)}
              </PalTypography>
            ) : (
              <PalTypography gutterBottom color="success.main" variant="body2">
                {t("dashboard.onboarding.shopifySyncCompleted")}
              </PalTypography>
            )
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
}

export default StepShopifyCompleted;
